import { useEffect } from "react";
import { Button, Loading, Modal, Typography } from "@/components/atoms";

interface UploadClientListModalProps {
  open: boolean;
  clientCount: number;
  isUploading?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Confirmation gate shown on top of the still-open spreadsheet import wizard, so
 * cancelling drops the operator back into the wizard with their file and column
 * mapping intact.
 *
 * Layering over that wizard needs two escapes: a stacking order above its Chakra
 * modal (z-1400), and standing down its focus lock — otherwise react-focus-lock
 * drags focus back into the wizard and these buttons can't be tabbed to.
 */
export const UploadClientListModal = ({ open, clientCount, isUploading, onCancel, onConfirm }: UploadClientListModalProps) => {
  useEffect(() => {
    if (!open) return;

    const locks = Array.from(document.querySelectorAll<HTMLElement>("[data-focus-lock-disabled]"));
    const previous = locks.map((lock) => lock.getAttribute("data-focus-lock-disabled"));
    locks.forEach((lock) => lock.setAttribute("data-focus-lock-disabled", "disabled"));

    return () => locks.forEach((lock, index) => lock.setAttribute("data-focus-lock-disabled", previous[index] ?? "false"));
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={isUploading ? undefined : onCancel}
      variant="web-small"
      zIndexClassName="z-[1500]"
      className="!w-[400px] !max-w-full"
      title="Upload Client List"
      description={`You're about to upload a bulk client list. Once confirmed, an email with login details and setup instructions will be automatically sent to all ${clientCount} client${
        clientCount === 1 ? "" : "s"
      } in this file.`}
      renderButtons={() => (
        <>
          <Button variant="secondary" size="lg" className="flex-1" onClick={onCancel} disabled={isUploading}>
            Cancel
          </Button>
          <Button variant="primary" size="lg" className="flex-1" onClick={onConfirm} disabled={isUploading}>
            Confirm
          </Button>
        </>
      )}
    >
      <Typography variant="paragraph" className="text-neutral-dark-gray">
        This action cannot be undone. Please make sure your file is correct and up to date before proceeding.
      </Typography>
      {/* Matches the dialog panel's radius — the overlay is inset-0, so square corners would paint over it. */}
      {isUploading && <Loading className="rounded-xl" />}
    </Modal>
  );
};
